import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';

const SEX = ['bull', 'cow', 'steer', 'heifer', 'calf'];
const POLLED = ['', 'PP', 'Pp', 'pp', 'unknown'];
const A2 = ['', 'A2A2', 'A2A1', 'A1A1', 'unknown'];
const STATUS = ['', 'carrier', 'non-carrier', 'unknown'];

export default function CattleNew() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    ear_tag: '', adca_reg_no: '', name: '', dob: '', sex: 'cow',
    color: '', polled_status: '', a2_status: '', pha_status: '', chondro_status: '',
    dam_id: '', sire_id: '', notes: '',
  });
  const [parents, setParents] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get('/api/cattle?all=1').then(({ cattle }) => setParents(cattle)).catch(e => setErr(e.message));
  }, []);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const body = {
        ...form,
        dam_id: form.dam_id ? Number(form.dam_id) : null,
        sire_id: form.sire_id ? Number(form.sire_id) : null,
        ear_tag: form.ear_tag.trim() || null,
        adca_reg_no: form.adca_reg_no.trim() || null,
        name: form.name.trim() || null,
        color: form.color.trim() || null,
        notes: form.notes.trim() || null,
        dob: form.dob || null,
        polled_status: form.polled_status || null,
        a2_status: form.a2_status || null,
        pha_status: form.pha_status || null,
        chondro_status: form.chondro_status || null,
      };
      const { id } = await api.post('/api/cattle', body);
      navigate(`/cattle/${id}`);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  const dams = parents.filter(p => p.sex === 'cow' || p.sex === 'heifer');
  const sires = parents.filter(p => p.sex === 'bull');

  return (
    <form onSubmit={submit} className="space-y-5 max-w-2xl">
      <h1 className="text-2xl font-serif text-olive-900">Add Animal</h1>

      <div className="card grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="label">Name</label>
          <input className="input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Big Mama" />
        </div>
        <div>
          <label className="label">Ear tag</label>
          <input className="input" value={form.ear_tag} onChange={e => set('ear_tag', e.target.value)} />
        </div>
        <div>
          <label className="label">ADCA Reg #</label>
          <input className="input" value={form.adca_reg_no} onChange={e => set('adca_reg_no', e.target.value)} />
        </div>
        <div>
          <label className="label">DOB</label>
          <input className="input" type="date" value={form.dob} onChange={e => set('dob', e.target.value)} />
        </div>
        <div>
          <label className="label">Sex</label>
          <select className="input" value={form.sex} onChange={e => set('sex', e.target.value)}>
            {SEX.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="col-span-2">
          <label className="label">Color</label>
          <input className="input" value={form.color} onChange={e => set('color', e.target.value)} placeholder="black, dun, red…" />
        </div>
      </div>

      <div className="card grid grid-cols-2 gap-3">
        <h2 className="col-span-2 font-medium text-olive-900">Genetics</h2>
        <div>
          <label className="label">Polled</label>
          <select className="input" value={form.polled_status} onChange={e => set('polled_status', e.target.value)}>
            {POLLED.map(s => <option key={s} value={s}>{s || '—'}</option>)}
          </select>
        </div>
        <div>
          <label className="label">A2/A2</label>
          <select className="input" value={form.a2_status} onChange={e => set('a2_status', e.target.value)}>
            {A2.map(s => <option key={s} value={s}>{s || '—'}</option>)}
          </select>
        </div>
        <div>
          <label className="label">PHA</label>
          <select className="input" value={form.pha_status} onChange={e => set('pha_status', e.target.value)}>
            {STATUS.map(s => <option key={s} value={s}>{s || '—'}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Chondro</label>
          <select className="input" value={form.chondro_status} onChange={e => set('chondro_status', e.target.value)}>
            {STATUS.map(s => <option key={s} value={s}>{s || '—'}</option>)}
          </select>
        </div>
      </div>

      <div className="card grid grid-cols-2 gap-3">
        <h2 className="col-span-2 font-medium text-olive-900">Lineage</h2>
        <div>
          <label className="label">Dam</label>
          <select className="input" value={form.dam_id} onChange={e => set('dam_id', e.target.value)}>
            <option value="">—</option>
            {dams.map(d => <option key={d.id} value={d.id}>{d.name || d.ear_tag || `#${d.id}`}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Sire</label>
          <select className="input" value={form.sire_id} onChange={e => set('sire_id', e.target.value)}>
            <option value="">—</option>
            {sires.map(s => <option key={s.id} value={s.id}>{s.name || s.ear_tag || `#${s.id}`}</option>)}
          </select>
        </div>
      </div>

      <div className="card">
        <label className="label">Notes</label>
        <textarea className="input" rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} />
      </div>

      {err && <div className="bg-rust-500/10 text-rust-700 rounded-md px-3 py-2 text-sm">{err}</div>}

      <div className="flex gap-2">
        <button type="button" onClick={() => navigate('/cattle')} className="btn-ghost">Cancel</button>
        <button className="btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
      </div>
    </form>
  );
}
